import React from "react";
import classnames from "classnames";
import {
    DisassociateIcon,
    EyeIcon,
    PencilIcon,
    SettingsIcon,
    TrashIcon,
} from "@aureskonnect/react-ui";
import { useTranslation } from "react-i18next/";
import { useSnapshot } from "valtio";
import { Tooltip } from "@mui/material";

import {
    store as appStore,
    setIsActionsClicked,
    setIsEdited,
    setActions,
} from "../store";
import {
    deviceStore,
    setEditedSelectedRows,
    setIsConfigurationCashManagementClicked,
    setIsConfigurationMoneticClicked,
    setIsConfigurationPrinterClicked,
} from "./store";

import TimePastSvgIcon from "@components/Common/SvgIcons/TimePastSvgIcon";
import ErrorToast from "@components/Common/ErrorTost";
import LinkSvgIcon from "@components/Common/SvgIcons/LinkSvgIcon";

interface CustomIconsProps {
    e: any;
    setSelectedRows: (rows: any) => void;
    setIsAddApplication: (value: boolean) => void;
    isApplicationArchived: boolean;
    setIsConfirmationModalArchived: (value: boolean) => void;
    setIsConfirmationModalActived: (value: boolean) => void;
    setIsDeviceAssociateModalOpened: (value: boolean) => void;
    setIsDeviceDissociateModalOpened: (value: boolean) => void;
    setEditedData: (data: any) => void;
    onOpenHistory: (row: any) => void;
}

export function CustomIcons({
    e,
    setSelectedRows,
    setIsAddApplication,
    isApplicationArchived,
    setIsConfirmationModalArchived,
    setIsConfirmationModalActived,
    setIsDeviceAssociateModalOpened,
    setIsDeviceDissociateModalOpened,
    onOpenHistory,
    setEditedData,
}: CustomIconsProps) {
    const { t } = useTranslation();
    const {
        isConfigurationPrinterClicked,
        isConfigurationCashManagementClicked,
        isConfigurationMoneticClicked,
    } = useSnapshot(deviceStore);
    const { modify } = useSnapshot(appStore);
    const { isActionsClicked, isEdited } = useSnapshot(appStore);

    const handleActionWithEditCheck = (action: () => void) => {
        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(() => {
                setIsEdited(false);
                action();
            });
        } else {
            action();
        }
    };

    const handleEditClick = () => {
        handleActionWithEditCheck(() => {
            // Forcer toujours à true: DynamicTable mémoise les rows, donc une
            // valeur conditionnelle lue via prop peut être périmée après une
            // fermeture du form. toggleModal côté parent est idempotent.
            setIsAddApplication(true);
            setSelectedRows([e.selectedRow]);
            setEditedData(e.selectedRow);
        });
    };

    const handleAssociateClick = () => {
        handleActionWithEditCheck(() => {
            setIsAddApplication(false);
            setEditedSelectedRows([]);
            setIsDeviceAssociateModalOpened(true);
            setSelectedRows([e.selectedRow]);
        });
    };

    const handleArchiveClick = () => {
        const archiveAction = () => {
            setIsAddApplication(false);
            if (e.selectedRow.subRows?.length > 0) {
                ErrorToast(
                    t(
                        "Please note that you cannot archive a application associated with an device."
                    )
                );
            } else {
                setIsConfirmationModalArchived(true);
            }
            setSelectedRows([e.selectedRow]);
        };

        handleActionWithEditCheck(archiveAction);
    };

    const handlePeripheralConfigClick = () => {
        const configAction = () => {
            setSelectedRows(e.selectedRow);

            const peripheralType = e.selectedRow.peripheralType;
            switch (peripheralType) {
                case "printer":
                    setIsConfigurationPrinterClicked(
                        !isConfigurationPrinterClicked
                    );
                    break;
                case "cash_management":
                    setIsConfigurationCashManagementClicked(
                        !isConfigurationCashManagementClicked
                    );
                    break;
                case "monetic":
                    setIsConfigurationMoneticClicked(
                        !isConfigurationMoneticClicked
                    );
                    break;
            }
        };

        handleActionWithEditCheck(configAction);
    };

    const handleDissociateClick = () => {
        handleActionWithEditCheck(() => {
            setSelectedRows([e.selectedRow]);
            setIsDeviceDissociateModalOpened(true);
        });
    };

    const handleHistoryClick = () => {
        handleActionWithEditCheck(() => {
            setSelectedRows([e.selectedRow]);
            onOpenHistory(e.selectedRow);
        });
    };
    const isApplicationRow =
        e.selectedRow?.subRows !== undefined &&
        e.selectedRow["Application"] !== "";
    const isPeripheralRow = e.selectedRow?.[t("Peripheral")] !== "";

    if (isApplicationRow) {
        return (
            <div className="d-flex justify-content-between mt-2">
                {modify && (
                    <>
                        <div>
                            <Tooltip
                                title={t("Modify an application").toString()}
                            >
                                <div>
                                    <PencilIcon
                                        onClick={handleEditClick}
                                        height={25}
                                        width={25}
                                        className={classnames(
                                            "cursor-pointer",
                                            "pointer__clz cmn_icn_edit",
                                            {
                                                "not-allowed-icon__clz": isApplicationArchived,
                                            }
                                        )}
                                    />
                                </div>
                            </Tooltip>
                        </div>
                        <div style={{ position: "relative", bottom: "3px" }}>
                            <Tooltip
                                title={t(
                                    "Associate a device with an application"
                                ).toString()}
                            >
                                <div>
                                    <LinkSvgIcon
                                        className={classnames(
                                            "pointer__clz cmn_icn_associate mr-1",
                                            {
                                                "not-allowed-icon__clz": isApplicationArchived,
                                            }
                                        )}
                                        onClick={handleAssociateClick}
                                        height={30}
                                        width={25}
                                        style={{
                                            position: "relative",
                                            top: "5px",
                                        }}
                                    />
                                </div>
                            </Tooltip>
                        </div>
                        <div>
                            {!isApplicationArchived ? (
                                <Tooltip title={t("Archive").toString()}>
                                    <div>
                                        <TrashIcon
                                            onClick={handleArchiveClick}
                                            height={25}
                                            width={25}
                                            fill="red"
                                            className={classnames(
                                                "cursor-pointer",
                                                "pointer__clz cmn_icn_FRarchived ml-1"
                                            )}
                                        />
                                    </div>
                                </Tooltip>
                            ) : (
                                <Tooltip title={t("Unarchive").toString()}>
                                    <div>
                                        <EyeIcon
                                            height={25}
                                            width={25}
                                            onClick={() => {
                                                setIsConfirmationModalActived(
                                                    true
                                                );
                                                setSelectedRows([
                                                    e.selectedRow,
                                                ]);
                                            }}
                                            className="cursor-pointer pointer__clz ml-1 cmn_icn_unarchive"
                                        />
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    </>
                )}
                <div>
                    <Tooltip title={t("Consult history").toString()}>
                        <div>
                            <TimePastSvgIcon
                                onClick={handleHistoryClick}
                                className="pointer__clz cmn_icn_history ml-2"
                            />
                        </div>
                    </Tooltip>
                </div>
            </div>
        );
    }

    if (isPeripheralRow) {
        const isMonetic = e.selectedRow.peripheralType === "monetic";
        const mark = e.selectedRow?.mark;
        const moneticHasSetup = ["Valina", "Nepting", "Valina swiss"].includes(
            mark
        );
        const moneticIsDisabled = mark === "Nepting" || mark === "Valina swiss";

        const settingsIcon =
            isMonetic && !moneticHasSetup ? null : (
                <Tooltip title={t("Configure").toString()}>
                    <div>
                        <SettingsIcon
                            className={classnames(
                                e.selectedRow.peripheralType === "printer"
                                    ? "cmn_icn_printSettings"
                                    : "",
                                {
                                    "not-allowed-icon__clz":
                                        isMonetic && moneticIsDisabled,
                                    pointer__clz: !(
                                        isMonetic && moneticIsDisabled
                                    ),
                                }
                            )}
                            onClick={handlePeripheralConfigClick}
                            height={22}
                            width={22}
                        />
                    </div>
                </Tooltip>
            );

        return (
            <div className="d-flex justify-content-between mt-3">
                {modify && (
                    <>
                        {settingsIcon}
                        <Tooltip
                            title={t(
                                "Unlink the device from the application"
                            ).toString()}
                        >
                            <div>
                                <DisassociateIcon
                                    height={40}
                                    width={35}
                                    fill="white"
                                    style={{
                                        position: "relative",
                                        bottom: "10px",
                                    }}
                                    className={classnames(
                                        "pointer__clz cmn_icn_dissociate ml-2",
                                        {
                                            "not-allowed-icon__clz": isApplicationArchived,
                                        }
                                    )}
                                    onClick={handleDissociateClick}
                                />
                            </div>
                        </Tooltip>
                    </>
                )}

                <Tooltip title={t("Consult history").toString()}>
                    <div>
                        <TimePastSvgIcon
                            onClick={handleHistoryClick}
                            className="pointer__clz cmn_icn_history ml-2"
                        />
                    </div>
                </Tooltip>
            </div>
        );
    }

    return null;
}
